// src/utils/replayPresignupHandles.ts
// Converts the pre-signup local handle list into authenticated ContactPoints.
// Called once per session from claimPrimaryIdsOnce (UserData.tsx), BEFORE
// replayPresignupList, so addCrushProducer sees confirmed handles in allConfirmed
// and fires correct edges + reciprocal detection in one pass.
//
// Two-step per entry:
//   a. POST attachContactPoint { kind:"handle", value, platform }
//      On ok:true  → persists contactPointId into that entry in localStorage.
//      On ALREADY_EXISTS with no stored contactPointId → skips step (b),
//        marks imported (ContactPoint exists server-side; user confirms manually).
//      On ALREADY_EXISTS with stored contactPointId → proceeds to step (b).
//      On other failure → leaves un-imported, retries next login.
//   b. AppSync client.mutations.confirmUserHandle({ contactPointId })
//      Treats both success and alreadyConfirmed:true as success.
//      On success → marks entry imported:true in localStorage.
//      On failure → leaves un-imported, retries next login.
//
// After all entries imported: clears "crushhh_presignup_handles" from localStorage.
//
// TRUST BOUNDARY: authed user context — called with a valid Cognito ID token.

import { generateClient } from "aws-amplify/data";
import type { Schema } from "../../amplify/data/resource";
import {
  HANDLES_LS_KEY,
  type PresignupHandle,
  type PresignupHandleStore,
} from "../components/LocalHandleList";

const client = generateClient<Schema>();

function readStore(): PresignupHandle[] {
  try {
    const raw = localStorage.getItem(HANDLES_LS_KEY);
    if (!raw) return [];
    const parsed: PresignupHandleStore = JSON.parse(raw);
    if (parsed.version !== 1 || !Array.isArray(parsed.entries)) return [];
    return parsed.entries;
  } catch {
    return [];
  }
}

function writeStore(entries: PresignupHandle[]): void {
  try {
    const store: PresignupHandleStore = { version: 1, entries };
    localStorage.setItem(HANDLES_LS_KEY, JSON.stringify(store));
  } catch {
    // storage quota full — non-fatal
  }
}

function patchAt(
  entries: PresignupHandle[],
  i: number,
  patch: Partial<PresignupHandle>,
): PresignupHandle[] {
  return entries.map((e, idx) => (idx === i ? { ...e, ...patch } : e));
}

export async function replayPresignupHandles(
  attachEndpoint: string,
  token: string,
): Promise<void> {
  let live = readStore();
  if (live.length === 0) return;

  for (let i = 0; i < live.length; i++) {
    const entry = live[i];
    if (entry.imported) continue;

    // ── Step A: attachContactPoint ──────────────────────────────────────────
    let contactPointId: string | undefined = entry.contactPointId;

    if (!contactPointId) {
      try {
        const resp = await fetch(attachEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token },
          body: JSON.stringify({
            kind: "handle",
            value: entry.handle,
            platform: entry.platform,
          }),
        });
        const json: any = await resp.json().catch(() => ({}));

        if (json?.ok && json?.contactPointId) {
          contactPointId = json.contactPointId as string;
          live = patchAt(live, i, { contactPointId });
          writeStore(live);
        } else if (json?.reason === "ALREADY_EXISTS") {
          // ContactPoint exists server-side; no contactPointId in ALREADY_EXISTS
          // response. Can't call confirmUserHandle without it — skip step B.
          // Mark imported to stop the retry loop; user confirms manually if needed.
          live = patchAt(live, i, { imported: true });
          writeStore(live);
          continue;
        } else {
          // Unexpected failure — leave un-imported, retry next login.
          continue;
        }
      } catch {
        // Network error — retryable.
        continue;
      }
    }

    // ── Step B: confirmUserHandle (AppSync mutation) ────────────────────────
    if (contactPointId) {
      try {
        const result = await client.mutations.confirmUserHandle({ contactPointId });
        const data: any = JSON.parse((result.data as string) ?? "{}");
        // data.success is true for both fresh confirms and alreadyConfirmed:true.
        if (data?.success) {
          live = patchAt(live, i, { imported: true });
          writeStore(live);
        }
        // Not success → leave un-imported, retry next login.
      } catch {
        // Non-fatal — leave un-imported, retry next login.
      }
    }
  }

  // Clear only after all entries are imported (mirrors crush list clear pattern).
  if (live.every((e) => e.imported)) {
    localStorage.removeItem(HANDLES_LS_KEY);
  }
}
